import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { authApi, getCachedUser } from '../lib/auth';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  name: string;
  path: string;
  icon: string;
}

const navItems: NavItem[] = [
  { name: 'Overview', path: '/dashboard', icon: '📊' },
  { name: 'Transactions', path: '/dashboard/transactions', icon: '💳' },
  { name: 'Static Addresses', path: '/dashboard/static-addresses', icon: '📬' },
  { name: 'API Keys', path: '/dashboard/api-keys', icon: '🔑' },
  { name: 'Webhooks', path: '/dashboard/webhooks', icon: '🔗' },
  { name: 'Settings', path: '/dashboard/settings', icon: '⚙️' }
];

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [partnerId, setPartnerId] = useState<string | null>(null);

  useEffect(() => {
    const user = getCachedUser();
    if (user?.partnerId) {
      setPartnerId(user.partnerId);
    }
  }, []);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await authApi.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still redirect to login even if API call fails
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}
      >
        {/* Logo/Brand */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-gray-900">PouchPay</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            >
              {sidebarOpen ? '←' : '→'}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                {sidebarOpen && <span>{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Menu / Logout */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg transition-colors ${
              isLoggingOut
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-red-50 hover:text-red-700'
            }`}
          >
            <span className="text-xl">🚪</span>
            {sidebarOpen && <span>{isLoggingOut ? 'Logging out...' : 'Logout'}</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Partner ID Header */}
        {partnerId && (
          <div className="bg-white border-b border-gray-200 px-8 py-3">
            <div className="container mx-auto max-w-7xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Partner ID:</span>
                  <code className="bg-gray-100 px-2 py-1 rounded font-mono text-gray-900">{partnerId}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(partnerId);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-xs"
                    title="Copy Partner ID"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="container mx-auto p-8 max-w-7xl">{children}</div>
      </main>
    </div>
  );
}
